import { Alert, Box, Button, IconButton } from '@mui/material';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { updatePlayedMedia } from 'store/data/played/actions';
import type { ApplicationState, Article } from 'types';
import { articleVideoPoster, articleVideoSource } from 'video/articleVideo';
import { activateInlineVideo, releaseInlineVideo } from 'video/playback';
import { MediaPlayer } from './MediaPlayer';

export function ArticleVideoPlayer({ article, onDeactivate }: { article: Article; onDeactivate?: () => void }) {
	const videoRef = useRef<HTMLVideoElement>(null);
	const wrapperRef = useRef<HTMLDivElement>(null);
	const dispatch = useDispatch();
	const resumeTime = useSelector((state: ApplicationState) => state.offlineState.playedMedia[article.id]?.time ?? 0);
	const initialTime = useRef(resumeTime);
	const lastSaved = useRef(0);
	const [message, setMessage] = useState('');
	const [expanded, setExpanded] = useState(false);
	const [inlineHeight, setInlineHeight] = useState<number>();
	const source = articleVideoSource(article);

	const save = (force: boolean) => {
		const video = videoRef.current;
		if (video && Number.isFinite(video.currentTime) && (force || Date.now() - lastSaved.current > 5000)) {
			lastSaved.current = Date.now();
			dispatch(updatePlayedMedia(article.id, video.currentTime, force));
		}
	};

	useEffect(() => {
		const video = videoRef.current;
		const visibility = () => {
			if (document.hidden) video?.pause();
		};
		document.addEventListener('visibilitychange', visibility);
		return () => {
			document.removeEventListener('visibilitychange', visibility);
			if (video) {
				dispatch(updatePlayedMedia(article.id, video.currentTime, true));
				releaseInlineVideo(video);
			}
		};
	}, [article.id, dispatch]);

	useEffect(() => {
		if (!expanded) return;
		const overflow = document.body.style.overflow;
		document.body.style.overflow = 'hidden';
		const close = () => setExpanded(false);
		const key = (event: KeyboardEvent) => {
			if (event.key === 'Escape') {
				if (window.history.state?.expandedVideo === article.id) window.history.back();
				else close();
			}
		};
		window.addEventListener('popstate', close);
		document.addEventListener('keydown', key);
		return () => {
			document.body.style.overflow = overflow;
			window.removeEventListener('popstate', close);
			document.removeEventListener('keydown', key);
		};
	}, [expanded, article.id]);

	return (
		<Box ref={wrapperRef} sx={{ height: expanded ? inlineHeight : undefined }}>
			<Box sx={expanded ? { position: 'fixed', inset: 0, zIndex: 1600, bgcolor: '#000', display: 'flex', flexDirection: 'column', p: 1 } : { bgcolor: '#000' }}>
				<Box sx={{ position: 'relative', minHeight: 0, ...(expanded ? { flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' } : {}) }}>
					<video
						ref={videoRef}
						src={source}
						poster={articleVideoPoster(article)}
						controls
						playsInline
						preload="metadata"
						style={{ display: 'block', width: '100%', height: expanded ? '100%' : undefined, aspectRatio: '16 / 9', objectFit: 'contain' }}
						aria-label={article.title.en || article.title.hi || 'Video Satsang'}
						onLoadedMetadata={() => {
							const video = videoRef.current!;
							if (initialTime.current > 0 && initialTime.current < video.duration - 2) video.currentTime = initialTime.current;
							void video.play().catch(() => setMessage('Tap play to start the video.'));
						}}
						onPlay={() => {
							setMessage('');
							activateInlineVideo(videoRef.current!, onDeactivate);
							MediaPlayer.instance()?.stopMusicPlayer();
						}}
						onPause={() => save(true)}
						onEnded={() => save(true)}
						onTimeUpdate={() => save(false)}
						onError={() => setMessage('The video could not be played. Check your connection or try again.')}
					/>
					<IconButton aria-label={expanded ? 'Close expanded video' : 'Expand video'} title={expanded ? 'Close expanded video' : 'Expand video'}
						sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1, color: '#fff', bgcolor: '#0009', '&:hover': { bgcolor: '#000c' } }} onClick={() => {
							if (expanded) {
								if (window.history.state?.expandedVideo === article.id) window.history.back();
								else setExpanded(false);
							} else {
								setInlineHeight(wrapperRef.current?.getBoundingClientRect().height);
								window.history.pushState({ ...window.history.state, expandedVideo: article.id }, '');
								setExpanded(true);
							}
						}}>{expanded ? <FullscreenExitIcon /> : <FullscreenIcon />}</IconButton>
				</Box>
				{message && <Alert severity="info" action={<Button onClick={() => {
					setMessage('');
					videoRef.current?.load();
				}}>Retry</Button>}>{message}</Alert>}
			</Box>
		</Box>
	);
}
